import "./PinEditor.scss";
import React, { useState, CSSProperties, useEffect } from "react";
import {
  IonButton,
  IonFooter,
  IonIcon,
  IonGrid,
  IonRow,
  IonCol,
  IonContent,
  IonHeader,
  IonToolbar,
  IonTitle,
} from "@ionic/react";
import { backspaceOutline } from "ionicons/icons";
import { useTracked } from "../../state";
import { Plugins } from "@capacitor/core";
import { identityService } from "../../services/vault";
import { AuthMode } from "@ionic-enterprise/identity-vault";
import { loadAuthMode } from "../../context/vault";
import {
  ShowToastErrorMessage,
  ShowToastSuccessMessage,
} from "../../utils/helpers/ToastMessage";
import clsx from "clsx";
import lockedIcon from "../../assets/svg/locked.svg";

type PinEditorProps = {
  setPasscode: boolean;
};

const { Storage } = Plugins;

const PinEditor: React.FC<PinEditorProps> = ({ setPasscode }) => {
  const [state, dispatch] = useTracked();
  const [prompt, setPrompt] = useState(
    setPasscode ? "Enter your new PIN" : "Enter your PIN"
  );
  const [pin, setPin] = useState("");
  const [verifyPin, setVerifyPin] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isInvalidPin, setIsInvalidPin] = useState(false);
  const [isFirstAttemptFailed, setIsFirstAttemptFailed] = useState(false);
  const [isPinAttemptFailed, setIsPinAttemptFailed] = useState(false);

  const gridStyle: CSSProperties = {
    paddingBottom: "32px",
  };

  // const title = setPasscode ? "Create PIN" : "Unlock";
  // const minPinLength = 6;
  const maxPinLength = 6;

  useEffect(() => {
    const PINFailedAttempts = state.PINFailedAttempts || 0;

    if (PINFailedAttempts > 0) {
      if (PINFailedAttempts >= 1 && PINFailedAttempts < 3) {
        setPin("");
        setIsFirstAttemptFailed(true);
      }

      if (PINFailedAttempts >= 3) {
        setIsFirstAttemptFailed(false);
        setIsPinAttemptFailed(true);
        if (pin) {
          // show toast during input attempt only.
          ShowToastErrorMessage("3 Failed PIN Atttempts");
        }
        setPin("");
      }

      setIsInvalidPin(true);
      setTimeout(() => {
        setIsInvalidPin(false);
      }, 1e3);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.PINFailedAttempts]);

  const append = (n: number) => {
    setErrorMessage("");
    const latestPin = pin + n.toString();
    setPin(latestPin);

    if (latestPin.length === 6) {
      if (setPasscode) {
        setTimeout(() => {
          enter(latestPin);
        }, 400);
      } else {
        enter(latestPin);
      }
    }
  };

  const deleteInput = () => {
    if (pin.length) {
      setPin(pin.slice(0, -1));
    }
  };

  const enter = async (latestPin: any) => {
    if (setPasscode) {
      if (!verifyPin) {
        setVerifyPin(latestPin);
        setPin("");
        setPrompt("Re - enter your new PIN");
      } else if (verifyPin !== latestPin) {
        setVerifyPin("");
        setPin("");
        setPrompt("Enter your PIN");
        setErrorMessage("PIN doesn’t match. Please try again.");
        setIsInvalidPin(true);
        setTimeout(() => {
          setIsInvalidPin(false);
        }, 1e3);
      } else {
        await Storage.set({ key: "enteredPincode", value: latestPin });
        await Storage.set({ key: "isPincodeOpen", value: "false" });
        dispatch({ type: "hideApplicationsPIN" });
        
        await identityService.setAuthMode(
          state.authMode === AuthMode.BiometricOnly
            ? AuthMode.BiometricAndPasscode
            : AuthMode.PasscodeOnly
        );

        const isPersonalizePIN = localStorage.getItem("isPersonalizePIN");
        // show message only when not on personalize process.
        if (!isPersonalizePIN) {
          ShowToastSuccessMessage("PIN was set successfully");
        } else {
          // remove any occuring personalize pin setup.
          localStorage.removeItem("isPersonalizePIN");
        }

        await updateVaultAuth();
      }
    } else {
      await Storage.set({ key: "enteredPincode", value: latestPin });
      await Storage.set({ key: "isPincodeOpen", value: "false" });
    }
  };

  // const cancelPIN = async () => {
  //   await Storage.set({ key: "enteredPincode", value: "" });
  //   await Storage.set({ key: "isPincodeOpen", value: "false" });
  //   dispatch({ type: "hideApplicationsPIN" });
  // };

  const updateVaultAuth = async () => {
    const { authMode, biometricType, hasSession, biometricsSupported } =
      await loadAuthMode();
    // console.log("authmode", authMode);
    // console.log("hasSession", hasSession);
    // console.log("biometricType", biometricType);
    // console.log("biometricsSupported", biometricsSupported);

    // getAlias, setAlias
    dispatch({
      type: "setVaultAuth",
      authMode,
      biometricType,
      hasSession,
      biometricsSupported,
    });
    // console.log('Vault with PIN updated..')
  };

  // const closePIN = async () => {
  //   dispatch({ type: "hideApplicationsPIN" });
  //   localStorage.setItem("isOnlyPin", "false");
  // };

  // wip
  const redirectToRecovery = async () => {
    await identityService.logout();
    localStorage.removeItem("isAppLocked");
    // await Storage.set({ key: "enteredPincode", value: "" });
    // await Storage.set({ key: "isPincodeOpen", value: "false" });
    // await updateVaultAuth();
    // dispatch({ type: "hideApplicationsPIN" });
    dispatch({ type: "logout", appId: process.env.REACT_APP_APP_ID });
  };

  return (
    <>
      {/* <IonHeader>
        <IonToolbar>
          <IonTitle>{title}</IonTitle>
          {!setPasscode ?
          <IonButtons slot='primary'>
            <IonButton icon-only onClick={cancelPIN}>
              <IonIcon icon={close}></IonIcon>
            </IonButton>
          </IonButtons>
          : 
          <IonButtons slot='primary'>
          <IonButton icon-only onClick={closePIN}>
            <IonIcon icon={close}></IonIcon>
          </IonButton>
        </IonButtons>}
        </IonToolbar>
      </IonHeader> */}

      {!setPasscode && (
        <IonHeader className="pin-editor-header ion-no-border">
          <IonToolbar>
            <IonTitle>
              <IonIcon className="lock-icon" icon={lockedIcon} />
            </IonTitle>
          </IonToolbar>
        </IonHeader>
      )}

      <IonContent className="ion-padding ion-text-center pin-editor-content">
        <div className="pin-editor">
          <div className="pin-title">{prompt}</div>
          <div className={clsx("pin-dots", { invalid: isInvalidPin })}>
            {Array.from(Array(6).keys()).map((n) => (
              <div
                key={n}
                className={`pin-dot ${n + 1 <= pin.length ? "dot-active" : ""}`}
              ></div>
            ))}
          </div>
          <div className="pin-error-msg">{errorMessage}</div>
          {isFirstAttemptFailed && (
            <div className="pin-failed-first-attempt-msg">
              You have only <strong>3 attempts</strong> to enter PIN.
              <br />
              After 3 attempts, you'll need to use the
              <br />
              <strong>Recover button</strong> to reset the application.
            </div>
          )}
          {isPinAttemptFailed && (
            <div className="pin-recover-container">
              <div className="title">
                If you want to enter Verifier,
                <br />
                please click this button
              </div>
              <IonButton
                color="danger"
                expand="block"
                shape="round"
                onClick={redirectToRecovery}
                routerDirection="root"
                routerLink="/"
              >
                Recover
              </IonButton>
              <div className="subtitle">
                You'll be redirected to the Recover Identity page
              </div>
            </div>
          )}
        </div>
      </IonContent>

      {!isPinAttemptFailed && (
        <IonFooter className="pin-editor-btns">
          <IonGrid style={gridStyle}>
            <IonRow>
              {[{ num: 1 }, { num: 2, str: "abc" }, { num: 3, str: "def" }].map(
                (k) => (
                  <IonCol key={k.num}>
                    <IonButton
                      expand="block"
                      onClick={() => append(k.num)}
                      disabled={pin.length === maxPinLength}
                    >
                      <div>
                        {k.num}
                        <br />
                        <span className="pad-letters">{k.str}</span>
                      </div>
                    </IonButton>
                  </IonCol>
                )
              )}
            </IonRow>
            <IonRow>
              {[
                { num: 4, str: "ghi" },
                { num: 5, str: "jkl" },
                { num: 6, str: "mno" },
              ].map((k) => (
                <IonCol key={k.num}>
                  <IonButton
                    expand="block"
                    onClick={() => append(k.num)}
                    disabled={pin.length === maxPinLength}
                  >
                    <div>
                      {k.num}
                      <br />
                      <span className="pad-letters">{k.str}</span>
                    </div>
                  </IonButton>
                </IonCol>
              ))}
            </IonRow>
            <IonRow>
              {[
                { num: 7, str: "pqrs" },
                { num: 8, str: "tuv" },
                { num: 9, str: "wxyz" },
              ].map((k) => (
                <IonCol key={k.num}>
                  <IonButton
                    expand="block"
                    onClick={() => append(k.num)}
                    disabled={pin.length === maxPinLength}
                  >
                    <div>
                      {k.num}
                      <br />
                      <span className="pad-letters">{k.str}</span>
                    </div>
                  </IonButton>
                </IonCol>
              ))}
            </IonRow>
            <IonRow>
              <IonCol></IonCol>
              <IonCol>
                <IonButton
                  expand="block"
                  onClick={() => append(0)}
                  disabled={pin.length === maxPinLength}
                >
                  0
                </IonButton>
              </IonCol>
              <IonCol>
                <IonButton
                  className="del-btn"
                  expand="block"
                  onClick={deleteInput}
                  disabled={!pin.length}
                >
                  <IonIcon icon={backspaceOutline}></IonIcon>
                </IonButton>
              </IonCol>
              {/* <IonCol>
              <IonButton
                fill="outline"
                color="secondary"
                expand="block"
                onClick={enter}
                disabled={pin.length < minPinLength}
              >
                Enter
              </IonButton>
            </IonCol> */}
            </IonRow>
          </IonGrid>
        </IonFooter>
      )}
    </>
  );
};

export default PinEditor;
